import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { API_BASE_URL, ENDPOINTS, DEFAULT_STORE_ID } from '../config';

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber?: string;
  userName?: string;
}

interface AuthContextType {
  user: User | null;
  login: (emailOrMobile: string, password: string) => Promise<{ success: boolean; message?: string; status?: number }>;
  register: (userData: Omit<User, 'id'> & { password: string; mobileNumber?: string }) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Error parsing saved user:', e);
      }
    }
  }, []);
  
  // When a token exists but no user is loaded (e.g., after refresh) or user has invalid ID, reconstruct user from token
  useEffect(() => {
    if ((!user || user.id === 0) && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const reconstructedUser: User = {
          id: parseInt(payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']) || parseInt(payload.nameid) || parseInt(payload.sub) || parseInt(payload.id) || 0,
          firstName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] || payload.given_name || 'User',
          lastName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] || payload.family_name || '',
          email: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || payload.email || '',
          mobileNumber: payload.mobileNumber || undefined,
          userName: payload.UserName || payload.username || payload.unique_name || ''
        };
        setUser(reconstructedUser);
        localStorage.setItem('user', JSON.stringify(reconstructedUser));
      } catch (e) {
        // If token cannot be decoded, do nothing
      }
    }
  }, [token, user]);

  const login = async (emailOrMobile: string, password: string): Promise<{ success: boolean; message?: string; status?: number }> => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userName: emailOrMobile, // Can be email or mobile number
          password: password
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
        console.error('Login error:', errorData);
        return { success: false, message: errorData.message || 'Login failed', status: response.status };
      }

      const data = await response.json();
      const authToken = data.token;

      if (authToken) {
        setToken(authToken);
        localStorage.setItem('authToken', authToken);

        // Decode token to get user info (simple base64 decode for JWT payload)
        try {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          const userData: User = {
            id: parseInt(payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']) || parseInt(payload.nameid) || parseInt(payload.sub) || parseInt(payload.id) || 0,
            firstName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] || payload.given_name || 'User',
            lastName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] || payload.family_name || '',
            email: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || emailOrMobile,
            mobileNumber: payload.mobileNumber || undefined,
            userName: payload.UserName || emailOrMobile
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          return { success: true };
        } catch (decodeError) {
          console.error('Error decoding token:', decodeError);
          // Fallback: create a basic user object
          const userData: User = {
            id: Date.now(),
            firstName: 'User',
            lastName: '',
            email: emailOrMobile,
            userName: emailOrMobile
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          return { success: true };
        }
      }

      return { success: false, message: 'No token received' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error' };
    }
  };

  const register = async (userData: Omit<User, 'id'> & { password: string; mobileNumber?: string; userName?: string }): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userName: userData.userName || userData.mobileNumber || userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          mobileNumber: userData.mobileNumber || '',
          storeId: DEFAULT_STORE_ID
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Registration failed' }));
        console.error('Registration error:', errorData);
        return false;
      }

      // After successful registration, automatically log in
      const loginIdentifier = userData.userName || userData.mobileNumber || userData.email || '';
      const loginResult = await login(loginIdentifier, userData.password);
      return loginResult.success;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isAuthenticated,
      token
    }}>
      {children}
    </AuthContext.Provider>
  );
};
